import classNames from 'classnames';
import { Leaderboard, LeaderboardEntry } from '@loyalty/shared-common/leaderboard-api-client';
import { defaultCurrencyImage, getLocalizedProperty, uuid } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { GenericComponent } from '../../components-tree';
import { LeaderboardV2TableSkeleton } from './LeaderboardV2TableSkeleton';
import { NameFormat } from '../../utils/naming';
import { ReactBootstrap } from '../../ReactBootstrap';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';
import { formatProfileName } from '../format-profile-name';

export interface LeaderboardV2TableComponentAttributes {
  showProfilePicture: boolean;
  showProfileName: boolean;
  profileNameFormat: NameFormat;
  currencyId: string;
  topLimit: string; // TODO migrate to number without breaking change
  aboveLimit: string; // TODO migrate to number without breaking change
  belowLimit: string; // TODO migrate to number without breaking change
}

export type LeaderboardV2TableComponent = GenericComponent<LeaderboardV2TableComponentAttributes>;

const CLASSNAME = 'qlfeditor-leaderboard__table';

export type LeaderboardV2TableWidgetBackendProps = WidgetProps<LeaderboardV2TableComponent>;

interface LeaderboardTableWidgetFrontendProps {
  id: string;
  attributes: LeaderboardV2TableComponent['attributes'];
  emptyLabel: string;
  positionLabel: string;
  pointsLabel: string;
  currencyPicture: string;
  leaderboardFrontendEntries: LeaderboardFrontendEntry[];
  currentProfileId: string;
  participantLabel: string;
}

interface LeaderboardFrontendEntry {
  id: string;
  score: string;
  position: number;
  profileName?: string | null;
  profilePicture?: string | null;
  indices: {
    current: number;
    // top = 0
    above: number;
    me: number;
    // below = me + 1
    end: number;
  };
}

export function serializeLeaderboardV2TableComponentProps({
  state,
  options,
  context,
}: LeaderboardV2TableWidgetBackendProps): LeaderboardTableWidgetFrontendProps {
  const { leaderboardData, profile, currencies } = state;
  const { t, locale, formatNumber } = context.i18n;

  const {
    profileNameFormat,
    currencyId,
    topLimit,
    aboveLimit,
    belowLimit,
    showProfilePicture,
    showProfileName,
  } = options.attributes;

  const emptyLabel = t('editor.leaderboard.list.empty');
  const positionLabel = t('editor.leaderboard.table.columns.position');
  const pointsLabel = t('editor.leaderboard.table.columns.points');
  const participantLabel = t('aria.leaderboard.table.columns.participant');
  const currency = currencies.find(({ id }) => id === currencyId);
  const currencyPicture = getLocalizedProperty(currency, 'image', locale) || defaultCurrencyImage;

  const leaderboard: Leaderboard = leaderboardData[currencyId] ?? { top: [] };
  const top = leaderboard.top?.slice(0, parseInt(topLimit, 10)) ?? [];
  const notInTop = notInBy(top, 'id');
  const unfilteredAbove =
    leaderboard.above && aboveLimit !== '0'
      ? leaderboard.above.slice(-parseInt(aboveLimit, 10)) // "-" because we want those closest to the user
      : [];
  const above = unfilteredAbove.filter(notInTop);
  const me = leaderboard.me?.filter(notInTop) ?? [];
  const below = leaderboard.below?.slice(0, parseInt(belowLimit, 10)).filter(notInTop) ?? [];
  const leaderboardEntries: LeaderboardEntry[] = [...top, ...above, ...me, ...below].sort(
    (a, b) => a.position - b.position,
  );
  const leaderboardFrontendEntries = leaderboardEntries.map((entry, index) => ({
    id: entry.id,
    position: entry.position,
    score: formatNumber(entry.score),
    profileName: showProfileName
      ? formatProfileName(entry.profile, profileNameFormat, context)
      : undefined,
    profilePicture: showProfilePicture ? entry.profile.picture : undefined,
    indices: {
      current: index,
      above: top.length,
      me: top.length + above.length,
      end: leaderboardEntries.length,
    },
  }));

  return {
    pointsLabel,
    positionLabel,
    attributes: options.attributes,
    id: options.id || options.attributes.id || uuid(),
    currencyPicture,
    leaderboardFrontendEntries,
    emptyLabel,
    currentProfileId: profile.id,
    participantLabel,
  };
}

export function LeaderboardV2TableComponent(props: LeaderboardTableWidgetFrontendProps) {
  return (
    <TranslationProvider loader={<LeaderboardV2TableSkeleton />}>
      <Component {...props} />
    </TranslationProvider>
  );
}

function Component(minProps: LeaderboardTableWidgetFrontendProps) {
  const {
    id,
    emptyLabel,
    pointsLabel,
    positionLabel,
    attributes,
    currencyPicture,
    leaderboardFrontendEntries,
    currentProfileId,
    participantLabel,
  } = minProps;
  const { t } = useTranslation('participant');
  const { class: className, showProfileName, showProfilePicture } = attributes;
  const showUserColumn = showProfileName || showProfilePicture;

  if (leaderboardFrontendEntries.length === 0) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div className="fal fa-users-crown fa-5x" style={{ marginBottom: 0 }} aria-hidden></div>
        <p> {emptyLabel}</p>
      </div>
    );
  }

  return (
    <table className={classNames(CLASSNAME, 'qlf-table', className)}>
      <caption className="sr-only">{`${t('aria.leaderboard.table.caption')}`}</caption>
      <thead>
        <tr>
          <th scope="col" className="position">
            {positionLabel}
          </th>
          {showUserColumn && (
            <th scope="col" className="participant">
              <i className="fas fa-users" aria-hidden />
              <span className="sr-only">{participantLabel}</span>
            </th>
          )}
          <th scope="col" className="currency">
            <i className="fas fa-usd-circle" aria-hidden />
            <span title={pointsLabel}>{pointsLabel}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {leaderboardFrontendEntries.map(entry => (
          <LeaderboardRow
            currencyPicture={currencyPicture}
            currentProfileId={currentProfileId}
            attributes={attributes}
            entry={entry}
            key={entry.id}
          />
        ))}
      </tbody>
    </table>
  );
}

interface leaderboardRowProps {
  currentProfileId: string;
  currencyPicture: string;
  entry: LeaderboardFrontendEntry;
  attributes: LeaderboardV2TableComponentAttributes;
}

function LeaderboardRow(rowProps: leaderboardRowProps) {
  const { t } = useTranslation('participant');
  const { currentProfileId, entry, currencyPicture, attributes } = rowProps;
  const { showProfileName, showProfilePicture } = attributes;
  const { id, profileName, profilePicture, indices, position, score } = entry;
  const showUserColumn = showProfileName || showProfilePicture;
  const distanceFromMe = Math.abs(indices.current - indices.me);
  const isTop = indices.current < indices.above;
  const isAbove = indices.above <= indices.current && indices.current < indices.me;
  const isBelow = indices.me < indices.current;
  const isAround = isAbove || isBelow;
  const aboveLength = indices.me - indices.above;
  const belowLength = indices.end - indices.me - 1;
  const isCurrentUser = id === currentProfileId;

  const style = {
    opacity: isAround ? 1 - (0.7 * distanceFromMe) / (isAbove ? aboveLength : belowLength) : 1,
  };

  // Generate the position description for screen readers
  const getPositionDescription = () => {
    if (isCurrentUser) {
      if (position === 1) {
        return t('aria.leaderboard.position.you.top');
      }
      return t('aria.leaderboard.position.you.ranked', { position });
    }

    if (isTop) {
      if (position === 1) {
        return t('aria.leaderboard.position.first.top');
      }
      return t('aria.leaderboard.position.top.ranked', { position });
    }

    if (isAbove) {
      const peopleCount = indices.me - indices.current;
      const translationKey =
        peopleCount === 1
          ? 'aria.leaderboard.position.person.above'
          : 'aria.leaderboard.position.people.above';
      return t(translationKey, { count: peopleCount, position });
    }

    if (isBelow) {
      const peopleCount = indices.current - indices.me;
      const translationKey =
        peopleCount === 1
          ? 'aria.leaderboard.position.person.below'
          : 'aria.leaderboard.position.people.below';
      return t(translationKey, { count: peopleCount, position });
    }

    return t('aria.leaderboard.position.ranked', { position });
  };

  return (
    <tr
      className={classNames(
        id === currentProfileId ? 'current' : undefined,
        isTop ? 'top' : undefined,
      )}
      style={style}
    >
      <td className="position">
        <span title={`${position}`} aria-hidden>
          {position}
        </span>
        <span className="sr-only">{getPositionDescription()}</span>
      </td>

      {showUserColumn && (
        <td className="participant">
          {showProfilePicture && profilePicture && <img src={profilePicture} aria-hidden />}
          {showProfileName && profileName && <span title={profileName}>{profileName}</span>}
        </td>
      )}

      <td className="currency">
        <span title={`${score}`}>{score}</span>
        {currencyPicture && <img src={currencyPicture} width="20px" height="20px" aria-hidden />}
      </td>
    </tr>
  );
}

function notInBy<T>(array: T[], key: keyof T): (value: T) => boolean {
  const set = new Set(array.map(element => element[key]));

  return value => !set.has(value[key]);
}

export function LeaderboardV2TableRenderer(
  props: LeaderboardV2TableWidgetBackendProps,
  document?: Document,
): string {
  const reactProps = serializeLeaderboardV2TableComponentProps(props);
  return renderToString(
    <ReactBootstrap
      component="LeaderboardV2TableComponent"
      props={reactProps}
      document={document}
      componentType="leaderboard-v2-table"
    />,
  );
}
